import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["datePicker", "slot"];
  static values = {
    workspace: String,
    site: String,
    businessHours: Object,
    successMessage: String,
    errorMessage: String,
    outsideHoursMessage: String,
  };

  connect() {
    this.draggedAppointment = null;
    this.originalSlot = null;
    this.wasDragging = false;

    this.boundTouchMove = this.handleTouchMove.bind(this);
    this.element.addEventListener("touchmove", this.boundTouchMove, {
      passive: true,
    });
  }

  disconnect() {
    this.element.removeEventListener("touchmove", this.boundTouchMove);
  }

  handleTouchMove() {
    // Handle touch events passively
  }

  slotAcceptsDrop(slot) {
    return slot?.dataset.available === "true";
  }

  // Date Navigation
  previousDay() {
    const currentDate = new Date(this.datePickerTarget.value);
    currentDate.setDate(currentDate.getDate() - 1);
    this.datePickerTarget.value = currentDate.toISOString().split("T")[0];
    this.changeDate();
  }

  nextDay() {
    const currentDate = new Date(this.datePickerTarget.value);
    currentDate.setDate(currentDate.getDate() + 1);
    this.datePickerTarget.value = currentDate.toISOString().split("T")[0];
    this.changeDate();
  }

  changeDate() {
    const url = new URL(window.location);
    url.searchParams.set("date", this.datePickerTarget.value);
    Turbo.visit(url);
  }

  // Drag & Drop
  dragStart(event) {
    const card = event.currentTarget.closest("[data-appointment-id]");
    if (!card) return;

    this.wasDragging = true;
    this.draggedAppointment = card;
    this.originalSlot = card.parentElement;
    card.classList.add("opacity-50", "scale-[0.98]");

    event.dataTransfer.setData(
      "application/json",
      JSON.stringify({
        appointmentId: card.dataset.appointmentId,
        providerId: card.dataset.providerId,
      })
    );
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setDragImage(card, 24, 24);
  }

  dragEnd(event) {
    const card = event.currentTarget.closest("[data-appointment-id]");
    card?.classList.remove("opacity-50", "scale-[0.98]");
    window.setTimeout(() => {
      this.wasDragging = false;
    }, 100);
    this.slotTargets.forEach((slot) =>
      slot.classList.remove(
        "border-indigo-500",
        "border-2",
        "border-rose-500/50",
        "bg-rose-50/20"
      )
    );
  }
  dragEnter(event) {
    const slot = event.target.closest("[data-appointment-board-target='slot']");
    if (!slot) return;

    if (!this.slotAcceptsDrop(slot)) {
      slot.classList.add("border-rose-500/50", "bg-rose-50/20");
      return;
    }

    slot.classList.add("border-indigo-500", "border-2");
  }

  dragLeave(event) {
    const slot = event.target.closest("[data-appointment-board-target='slot']");
    if (slot) {
      slot.classList.remove(
        "border-indigo-500",
        "border-2",
        "border-rose-500/50",
        "bg-rose-50/20"
      );
    }
  }

  allowDrop(event) {
    const slot = event.target.closest("[data-appointment-board-target='slot']");
    if (!slot) return;

    const slotTime = slot.dataset.time;
    if (!slotTime) {
      console.error("No time data for slot");
      return;
    }

    try {
      if (!this.slotAcceptsDrop(slot)) {
        return;
      }
      event.preventDefault();
    } catch (error) {
      console.error("Error in allowDrop:", error);
    }
  }

  async drop(event) {
    event.preventDefault();
    const slot = event.target.closest("[data-appointment-board-target='slot']");
    if (!slot || !this.draggedAppointment) return;

    const slotTime = slot.dataset.time;
    if (!this.slotAcceptsDrop(slot)) {
      this.showToast(this.outsideHoursMessageValue, "warning");
      return;
    }

    const providerId = slot.dataset.providerId;
    const data = JSON.parse(event.dataTransfer.getData("application/json"));

    try {
      const result = await this.updateAppointment(
        data.appointmentId,
        providerId,
        slotTime
      );

      // Update the appointment's data attributes
      this.draggedAppointment.dataset.providerId = providerId;
      let appointmentContainer = slot.querySelector("[data-slot-appointments]");
      if (!appointmentContainer) {
        appointmentContainer = document.createElement("div");
        appointmentContainer.className =
          slot.dataset.slotLayout === "solo"
            ? "absolute inset-0 p-1"
            : "space-y-1 p-1.5";
        appointmentContainer.dataset.slotAppointments = "";
        slot.appendChild(appointmentContainer);
      }
      appointmentContainer.appendChild(this.draggedAppointment);
      this.showToast(
        result.message || this.successMessageValue,
        "success"
      );
    } catch (error) {
      this.originalSlot.appendChild(this.draggedAppointment);
      this.showToast(error.message || this.errorMessageValue, "error");
    } finally {
      this.draggedAppointment.classList.remove("opacity-50");
      this.slotTargets.forEach((slot) =>
        slot.classList.remove(
          "border-indigo-500",
          "border-2",
          "border-rose-500/50",
          "bg-rose-50/20"
        )
      );
    }
  }

  async openAppointment(event) {
    if (this.wasDragging || event.target.closest("[data-drag-handle]")) return;

    const card = event.currentTarget.closest("[data-appointment-url]");
    const url = card?.dataset.appointmentUrl;
    if (!url) return;

    try {
      const response = await fetch(url, {
        headers: {
          Accept: "text/vnd.turbo-stream.html",
          "X-Requested-With": "XMLHttpRequest",
        },
      });
      if (!response.ok) throw new Error("Unable to load appointment");

      window.Turbo.renderStreamMessage(await response.text());
    } catch (_error) {
      window.Turbo.visit(url);
    }
  }

  // API Calls
  async updateAppointment(appointmentId, providerId, time) {
    const token = document.querySelector('meta[name="csrf-token"]').content;
    const date = this.datePickerTarget.value;
    const startTime = `${date}T${time}:00`;

    try {
      const response = await fetch(
        `/workspaces/${this.workspaceValue}/sites/${this.siteValue}/appointments/${appointmentId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": token,
            Accept: "application/json",
          },
          body: JSON.stringify({
            appointment: {
              service_provider_id: providerId,
              start_time: startTime,
            },
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        const errorMessage = result.errors?.join(", ") || "Update failed";
        console.error("Validation errors:", result.errors);
        console.error("Debug info:", result.debug);
        throw new Error(errorMessage);
      }

      return result;
    } catch (error) {
      console.error("Error updating appointment:", error);
      throw error;
    }
  }
  // Notifications
  showToast(message, type = "success") {
    const container = document.getElementById("toasts") || document.body;
    const toast = document.createElement("div");
    const tones = {
      success: {
        shell:
          "border-emerald-200 bg-white dark:border-emerald-500/25 dark:bg-slate-900",
        icon: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
        symbol: "✓",
      },
      error: {
        shell:
          "border-rose-200 bg-white dark:border-rose-500/25 dark:bg-slate-900",
        icon: "bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300",
        symbol: "!",
      },
      warning: {
        shell:
          "border-amber-200 bg-white dark:border-amber-500/25 dark:bg-slate-900",
        icon: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
        symbol: "!",
      },
    };
    const tone = tones[type] || tones.success;

    toast.className = `pointer-events-auto flex w-[min(22rem,calc(100vw-2rem))] translate-y-2 items-start gap-3 rounded-xl border p-3.5 opacity-0 shadow-xl shadow-slate-950/10 transition duration-200 ${tone.shell}`;
    toast.setAttribute("role", type === "error" ? "alert" : "status");

    const icon = document.createElement("span");
    icon.className = `flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-sm font-bold ${tone.icon}`;
    icon.textContent = tone.symbol;

    const copy = document.createElement("p");
    copy.className =
      "min-w-0 flex-1 pt-1 text-sm font-medium text-slate-800 dark:text-slate-100";
    copy.textContent = message;

    const close = document.createElement("button");
    close.type = "button";
    close.className =
      "rounded-md p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200";
    close.setAttribute("aria-label", "Close");
    close.textContent = "×";
    close.addEventListener("click", () => this.dismissToast(toast));

    toast.append(icon, copy, close);
    container.appendChild(toast);
    requestAnimationFrame(() => {
      toast.classList.remove("translate-y-2", "opacity-0");
    });

    window.setTimeout(() => this.dismissToast(toast), 4000);
  }

  dismissToast(toast) {
    if (!toast?.isConnected) return;

    toast.classList.add("-translate-y-2", "opacity-0");
    window.setTimeout(() => toast.remove(), 200);
  }
}
